import { Fragment, useEffect, useMemo, useState } from "react"
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { Lock, Pencil, Plus } from "lucide-react"
import type { ReportingEntity } from "@pishgam/contracts"

import { api } from "../api"
import { listKeys, LoadingCard, messageFor } from "../shared"
import { monthLabel, NumberCell, SaveBar, sheet, SheetTable, sumDisplay, useEdits } from "@/components/spreadsheet"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"

export type Kind = "revenue" | "cost" | "kpi" | "assumption"
type Scenario = "actual" | "forecast"
type Month = { year: number; month: number; label: string; actualOpen: boolean }
type LineDef = { id: number; code: string; labelFa: string; kind: Kind; isPersonnel: boolean; isActive?: boolean }
type CellValue = { lineDefId: number; year: number; month: number; scenario: Scenario; value: string }
type Unit = { id: number; nameFa: string; reportingEntity: ReportingEntity }
type UnitGrid = { unit: Unit; months: Month[]; lines: LineDef[]; values: CellValue[] }

const KIND_TITLE: Record<Kind, string> = { revenue: "درآمدها", cost: "هزینه‌ها", kpi: "شاخص‌های عملکرد", assumption: "فرض‌های برنامه" }
const KIND_LABEL: Record<Kind, string> = { revenue: "درآمد", cost: "هزینه", kpi: "شاخص عملکرد", assumption: "فرض برنامه" }
const SCENARIO: Record<Scenario, string> = { actual: "محقق", forecast: "پیش‌بینی" }
const keyOf = (lineId: number, year: number, month: number, scenario: Scenario) => `${lineId}|${year}|${month}|${scenario}`

/**
 * Plan vs actual for one fiscal year, spreadsheet style: business → line → «محقق» and
 * «پیش‌بینی» rows, 12 month columns, plus revenue/cost/profit summary rows per business.
 * `editable` decides which scenario can be typed into (the server enforces the same rule):
 * finance types actuals, business heads type forecasts.
 */
export function PlanPanel({ fy, round, unitIds, editable, kinds, canAddLines, includeCorporate, onDirty }: {
  fy: number; round: number; unitIds?: number[]; editable: Scenario | "none"; kinds: Kind[]; canAddLines?: boolean
  /** Also show the company-level bucket (non-operating income), after the businesses. */
  includeCorporate?: boolean
  onDirty?: (count: number) => void
}) {
  const client = useQueryClient()
  const organization = useQuery({ queryKey: ["organization"], queryFn: () => api<{ businessUnits: Unit[]; corporate?: Unit | null }>("/organization") })
  const corporateId = organization.data?.corporate?.id
  const units = [
    ...(organization.data?.businessUnits ?? []).filter((unit) => !unitIds || unitIds.includes(unit.id)),
    ...(includeCorporate && organization.data?.corporate ? [organization.data.corporate] : []),
  ]
  const grids = useQueries({ queries: units.map((unit) => ({ queryKey: ["finance-unit", fy, round, unit.id], queryFn: () => api<UnitGrid>(`/finance/entry/${fy}/${round}/unit/${unit.id}`) })) })
  const loaded = grids.length > 0 && grids.every((grid) => grid.data)
  const stamp = grids.map((grid) => grid.dataUpdatedAt).join(",")
  const original = useMemo(() => {
    if (!loaded) return undefined
    return new Map(grids.flatMap((grid) => grid.data!.values.map((cell) => [keyOf(cell.lineDefId, cell.year, cell.month, cell.scenario), cell.value] as const)))
  }, [loaded, stamp]) // eslint-disable-line react-hooks/exhaustive-deps
  const edits = useEdits(original)
  useEffect(() => onDirty?.(edits.dirty), [edits.dirty, onDirty])
  const save = useMutation({
    mutationFn: () => api("/finance/entry", { method: "PUT", body: JSON.stringify({
      fiscalYear: fy, round,
      cells: edits.entries().map(([key, value]) => { const [lineDefId, year, month, scenario] = key.split("|"); return { lineDefId: Number(lineDefId), year: Number(year), month: Number(month), scenario, value: value === "" ? null : value } }),
    }) }),
    onSuccess: () => Promise.all(["finance-unit", "fpa", "pnl-fiscal-year", "process-tasks", "process-status"].map((key) => client.invalidateQueries({ queryKey: [key] }))),
  })
  const [lineDialog, setLineDialog] = useState<{ unitId: number; line: LineDef | null } | null>(null)

  if (!organization.data || !loaded) return <LoadingCard />
  if (!units.length) return <p className="text-sm text-muted-foreground">کسب‌وکاری برای نمایش وجود ندارد.</p>
  const months = grids[0]!.data!.months
  const value = (lineId: number, month: Month, scenario: Scenario) => Number(edits.get(keyOf(lineId, month.year, month.month, scenario))) || 0

  // Input rows in visual order so Enter/arrows and paste walk down the sheet.
  type InputRow = { line: LineDef; scenario: Scenario; unit: Unit }
  const inputRows: InputRow[] = []
  const sections = grids.map((grid) => {
    const unit = grid.data!.unit
    const active = grid.data!.lines.filter((line) => line.isActive !== false)
    const groups = kinds.map((kind) => ({ kind, lines: active.filter((line) => line.kind === kind) })).filter((group) => group.lines.length)
    for (const group of groups) for (const line of group.lines) for (const scenario of ["actual", "forecast"] as const) inputRows.push({ line, scenario, unit })
    return { unit, lines: active, groups }
  })
  const cellOpen = (row: InputRow, month: Month) =>
    editable === row.scenario && (row.scenario === "forecast" || month.actualOpen) && (row.line.kind !== "assumption" || month === months[0])
  // Why a cell is locked, in the user's words — shown as the cell tooltip.
  const lockReason = (row: InputRow, month: Month) => {
    if (editable !== row.scenario) return editable === "none" ? "این صفحه فقط خواندنی است" : `ردیف «${SCENARIO[row.scenario]}» در این صفحه فقط خواندنی است`
    if (row.scenario === "actual" && !month.actualOpen) return "این ماه برای ثبت محقق بسته است"
    if (row.line.kind === "assumption" && month !== months[0]) return "فرض برنامه فقط در ماه اول وارد می‌شود"
    return null
  }
  const pasteFrom = (rowIndex: number, col: number) => (matrix: string[][]) => edits.setMany(matrix.flatMap((cells, i) => cells.map((cell, j): [string, string] | null => {
    const row = inputRows[rowIndex + i]; const month = months[col + j]
    return row && month && cellOpen(row, month) ? [keyOf(row.line.id, month.year, month.month, row.scenario), cell] : null
  }).filter((entry): entry is [string, string] => entry !== null)))
  const total = (lines: LineDef[], kind: Kind, scenario: Scenario, month?: Month) =>
    lines.filter((line) => line.kind === kind).reduce((sum, line) => sum + (month ? value(line.id, month, scenario) : months.reduce((inner, item) => inner + value(line.id, item, scenario), 0)), 0)
  let rowIndex = -1
  const span = months.length + 2

  return (
    <>
      <p className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label="راهنمای رنگ ردیف‌ها">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block size-3 rounded-sm bg-emerald-100 ring-1 ring-emerald-700" /> محقق</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block size-3 rounded-sm bg-sky-100 ring-1 ring-sky-700" /> پیش‌بینی</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden className="inline-block size-3 rounded-sm bg-neutral-200 ring-1 ring-neutral-400" /> بسته (فقط خواندنی)</span>
      </p>
      <SheetTable label="برنامه و عملکرد">
        <thead className="sticky top-0 z-20">
          <tr>
            <th className={cn(sheet.corner, "min-w-60")}>کسب‌وکار / ردیف</th>
            {months.map((month) => <th key={`${month.year}-${month.month}`} className={cn(sheet.th, "min-w-24")}>{monthLabel(month.year, month.month)}</th>)}
            <th className={cn(sheet.th, "min-w-24")}>جمع</th>
          </tr>
        </thead>
        <tbody>
          {sections.map(({ unit, lines, groups }) => (
            <Fragment key={unit.id}>
              <tr>
                <th colSpan={span} className="border border-neutral-300 bg-success/10 px-3 py-1.5 text-right text-sm font-bold">
                  <span className="sticky right-3">{unit.nameFa}{unit.reportingEntity === "faraz" && <span className="ms-1 text-xs font-normal text-muted-foreground">(سود و زیان جداگانه)</span>}{unit.id === corporateId && <span className="ms-1 text-xs font-normal text-muted-foreground">(سطح شرکت؛ جدا از کسب‌وکارها، فقط در سود و زیان تجمیعی)</span>}</span>
                  {canAddLines && <Button variant="ghost" size="sm" className="ms-3 h-6 px-2 text-xs" onClick={() => setLineDialog({ unitId: unit.id, line: null })}><Plus className="size-3.5" /> ردیف</Button>}
                </th>
              </tr>
              {groups.map((group) => (
                <Fragment key={group.kind}>
                  {kinds.length > 1 && <tr><th colSpan={span} className={sheet.section}>{KIND_TITLE[group.kind]}</th></tr>}
                  {group.lines.flatMap((line) => (["actual", "forecast"] as const).map((scenario) => {
                    rowIndex += 1
                    const row = rowIndex
                    const input = inputRows[row]!
                    const readOnlyRow = editable !== scenario
                    return (
                      <tr key={`${line.id}-${scenario}`}>
                        <th scope="row" className={cn(sheet.rowHead, "border-s-4 py-0", scenario === "actual" ? "border-s-emerald-700 bg-emerald-50 dark:bg-emerald-950/40" : "border-s-sky-700 bg-sky-100 dark:bg-sky-950/40")}>
                          <span className="flex min-h-7 items-center gap-1.5">
                            <span className={cn("inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-px text-xs font-medium text-white", scenario === "actual" ? "bg-emerald-700" : "bg-sky-700")}>
                              {readOnlyRow ? <Lock className="size-2.5" aria-hidden /> : <Pencil className="size-2.5" aria-hidden />}{SCENARIO[scenario]}
                              <span className="sr-only">{readOnlyRow ? " (فقط خواندنی)" : " (قابل ویرایش)"}</span>
                            </span>
                            {scenario === "actual" ? (
                              <span className={cn("truncate font-medium", line.kind === "revenue" && "text-blue-800", line.kind === "cost" && "text-rose-800")}>{line.labelFa}</span>
                            ) : <span className="sr-only">{line.labelFa}</span>}
                            {scenario === "actual" && canAddLines && <button type="button" aria-label={`ویرایش ${line.labelFa}`} className="rounded p-0.5 text-muted-foreground opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none" onClick={() => setLineDialog({ unitId: unit.id, line })}><Pencil className="size-3" aria-hidden /></button>}
                          </span>
                        </th>
                        {months.map((month, col) => {
                          const key = keyOf(line.id, month.year, month.month, scenario)
                          const open = cellOpen(input, month)
                          return (
                            <td key={key} className={sheet.td}>
                              <NumberCell grid="plan" row={row} col={col} value={edits.get(key)} edited={edits.isEdited(key)} disabled={!open} tone={scenario}
                                label={`${unit.nameFa} — ${line.labelFa} — ${SCENARIO[scenario]} — ${month.label}`} title={open ? undefined : (lockReason(input, month) ?? undefined)}
                                onChange={(next) => edits.set(key, next)} onPasteGrid={pasteFrom(row, col)} />
                            </td>
                          )
                        })}
                        <td className={sheet.total}>{line.kind === "assumption" ? "" : sumDisplay(months.reduce((sum, month) => sum + value(line.id, month, scenario), 0))}</td>
                      </tr>
                    )
                  }))}
                </Fragment>
              ))}
              {/* Read-only summary: what the business made, plan vs actual. */}
              {([["revenue", "جمع درآمد"], ["cost", "جمع هزینه"], ["profit", "سود / (زیان)"]] as const).flatMap(([kind, title]) => (["actual", "forecast"] as const).map((scenario) => {
                const at = (month?: Month) => kind === "profit" ? total(lines, "revenue", scenario, month) - total(lines, "cost", scenario, month) : total(lines, kind, scenario, month)
                const strong = kind === "profit"
                return (
                  <tr key={`${unit.id}-${kind}-${scenario}`} className="bg-neutral-50 dark:bg-muted/40">
                    <th scope="row" className={cn(sheet.rowHead, "bg-neutral-50 text-xs dark:bg-muted/40", strong && "font-semibold")}>{title} — {SCENARIO[scenario]}</th>
                    {months.map((month) => { const amount = at(month); return <td key={`${month.year}-${month.month}`} className={cn(sheet.total, "font-normal", strong && "font-semibold", amount < 0 && "text-loss")}>{sumDisplay(amount)}</td> })}
                    {(() => { const amount = at(); return <td className={cn(sheet.total, strong && "font-semibold", amount < 0 && "text-loss")}>{sumDisplay(amount)}</td> })()}
                  </tr>
                )
              }))}
            </Fragment>
          ))}
        </tbody>
      </SheetTable>
      <SaveBar edits={edits} save={save} />
      {lineDialog && <LineDialog unitId={lineDialog.unitId} line={lineDialog.line} kinds={kinds} onClose={() => setLineDialog(null)} onSaved={() => { setLineDialog(null); void client.invalidateQueries({ queryKey: ["finance-unit"] }) }} />}
    </>
  )
}

function LineDialog({ unitId, line, kinds, onClose, onSaved }: { unitId: number; line: LineDef | null; kinds: Kind[]; onClose: () => void; onSaved: () => void }) {
  const [labelFa, setLabel] = useState(line?.labelFa ?? "")
  const [kind, setKind] = useState<Kind>(line?.kind ?? kinds[0]!)
  const [isActive, setActive] = useState(line?.isActive ?? true)
  const mutation = useMutation({
    mutationFn: () => line
      ? api(`/finance/lines/${line.id}`, { method: "PATCH", body: JSON.stringify({ labelFa, isActive }) })
      : api("/finance/lines", { method: "POST", body: JSON.stringify({ businessUnitId: unitId, labelFa, kind, isPersonnel: false, isActive: true }) }),
    onSuccess: onSaved,
  })
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{line ? "ویرایش ردیف" : "افزودن ردیف"}</DialogTitle>
          <DialogDescription>{line ? `نوع: ${KIND_LABEL[line.kind]}` : "ردیف جدید در همه دوره‌های برنامه این کسب‌وکار نمایش داده می‌شود."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); mutation.mutate() }}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="line-label">عنوان</FieldLabel>
              <Input id="line-label" value={labelFa} onChange={(event) => setLabel(event.target.value)} required maxLength={200} autoFocus />
            </Field>
            {!line && kinds.length > 1 && (
              <div role="radiogroup" aria-label="نوع ردیف" className="flex flex-wrap gap-2" onKeyDown={(event) => listKeys(event, "radio", (index) => setKind(kinds[index]!), false)}>
                {kinds.map((value) => (
                  <button key={value} type="button" role="radio" aria-checked={kind === value} tabIndex={kind === value ? 0 : -1} onClick={() => setKind(value)}
                    className={cn("rounded-md border px-3 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none", kind === value ? "border-primary bg-primary text-primary-foreground" : "bg-background hover:border-primary")}>{KIND_LABEL[value]}</button>
                ))}
              </div>
            )}
            {line && <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-primary" checked={isActive} onChange={(event) => setActive(event.target.checked)} /> فعال (ردیف غیرفعال در جمع‌ها حساب نمی‌شود)</label>}
            {mutation.error && <FieldError role="alert">{messageFor(mutation.error)}</FieldError>}
            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending || !labelFa.trim()}>{mutation.isPending ? "در حال ذخیره…" : "ذخیره"}</Button>
              <Button type="button" variant="ghost" onClick={onClose}>انصراف</Button>
            </DialogFooter>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  )
}
